import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config/config';
import { showToast } from '../../components/utils/Toast';
import { DEFAULT_PROPOSAL_TEMPLATE, generateItemsHtml } from '../../data/defaultProposalTemplate';
import { resolveRepInitials } from '../../utils/repInitials';

// Add this helper function to generate proposal template
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '0.00';
  }
  // Ensure we have a clean number, remove any existing commas or dollar signs
  const cleanAmount = typeof amount === 'string' ? 
    parseFloat(amount.replace(/[$,]/g, '')) : 
    Number(amount);
  
  if (isNaN(cleanAmount)) {
    return '0.00';
  }
  
  return cleanAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

export const generateProposalTemplate = (lead, proposal, items, displaySettings = {
  expandGroups: false,
  separateGroups: false
}) => {
  // Format the complete address by concatenating all address fields
  const formatAddress = (address) => {
    if (!address) return '';
    const parts = [
      address.street,
      address.city,
      address.state,
      address.zipCode
    ].filter(Boolean);
    return parts.join(', ');
  };

  // Helper function to convert description text to HTML list
  const formatDescriptionAsList = (description) => {
    if (!description) return '';
    
    // Split by newlines or periods followed by a space
    const points = description
      .split(/\n|(?<=\.)\s+/)
      .filter(point => point.trim().length > 0);
    
    if (points.length <= 1) {
      return `<div class="text-base text-gray-700 mt-3 leading-relaxed">${description}</div>`;
    }
    
    return `
      <ul class="list-disc pl-5 mt-3 space-y-1">
        ${points.map(point => `<li class="text-base text-gray-700 leading-relaxed">${point.trim()}</li>`).join('')}
      </ul>
    `;
  };

  // Get client name and job address
  const clientName = lead.client?.name || lead.clientName || '';
  const jobAddress = formatAddress(lead.siteContact?.address || {});

  // Separate items into grouped and non-grouped
  const groupedItems = items.filter(item => item.type === 'scope');
  const nonGroupedItems = items.filter(item => item.type === 'cost');

  // Calculate totals for grouped and non-grouped items
  const groupedTotal = groupedItems.reduce((sum, item) => sum + (Number(item.clientPrice) || 0), 0);
  const nonGroupedTotal = nonGroupedItems.reduce((sum, item) => sum + (Number(item.clientPrice) || 0), 0);
  const totalClientPrice = groupedTotal + nonGroupedTotal;

  // Helper function to render photos for an item
  const renderPhotos = (item) => {
    if (!item.photos || item.photos.length === 0) return '';
    
    return `
      <tr>
        <td colspan="4" class="py-4">
          <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
            ${item.photos.map(photo => `
              <div class="rounded-lg overflow-hidden border border-gray-300">
                <img src="${photo.fileUrl}" alt="${photo.fileName || 'Scope photo'}" class="w-full h-48 object-cover" />
              </div>
            `).join('')}
          </div>
        </td>
      </tr>
    `;
  };

  // Generate HTML for items based on display settings
  const generateItemsSection = (sectionItems) => {
    if (!sectionItems.length) return '';

    let html = `
      <table class="w-full mb-4">
        <tbody>
    `;

    // Add rows for each item
    sectionItems.forEach(item => {
      html += `
        <tr class="border-b border-gray-200">
          <td class="py-5">
            <div class="font-semibold text-lg">${item.item || ''}</div>
            ${displaySettings.showScopeDescription ? formatDescriptionAsList(item.description || '') : ''}
          </td>
          ${displaySettings.showQuantity ? `<td class="text-right py-5 align-top text-base">${item.quantity || ''}</td>` : ''}
          ${displaySettings.showUnit ? `<td class="text-right py-5 align-top text-base">${item.unit || ''}</td>` : ''}
          ${displaySettings.showPrice ? `<td class="text-right py-5 align-top text-base font-medium">$${formatCurrency(item.clientPrice || 0)}</td>` : ''}
        </tr>
      `;

      // Add photos for scope items
      if (item.type === 'scope') {
        html += renderPhotos(item);
      }

      // If it's a grouped item and groups are expanded, show its cost codes
      if (item.type === 'scope' && item.costCodes && (displaySettings.expandGroups ?? false)) {
        item.costCodes.forEach(costCode => {
          html += `
            <tr class="border-b border-gray-100 bg-gray-50">
              <td class="py-4 pl-8">
                <div class="text-base font-medium">${costCode.item || ''}</div>
                ${displaySettings.showScopeDescription ? formatDescriptionAsList(costCode.description || '') : ''}
              </td>
              ${displaySettings.showQuantity ? `<td class="text-right py-4 align-top text-base">${costCode.quantity || ''}</td>` : ''}
              ${displaySettings.showUnit ? `<td class="text-right py-4 align-top text-base">${costCode.unit || ''}</td>` : ''}
              ${displaySettings.showPrice ? `<td class="text-right py-4 align-top text-base">$${formatCurrency(costCode.clientPrice || 0)}</td>` : ''}
            </tr>
          `;
        });
      }
    });

    // Add section total if enabled
    if (displaySettings.showSeparateTotals) {
      const sectionTotal = sectionItems.reduce((sum, item) => sum + (Number(item.clientPrice) || 0), 0);
      html += `
        <tr class="border-t border-gray-300">
          <td colspan="${displaySettings.showScopeDescription ? '2' : '1'}" class="py-5 font-semibold text-base">Subtotal</td>
          ${displaySettings.showQuantity ? '<td></td>' : ''}
          ${displaySettings.showUnit ? '<td></td>' : ''}
          ${displaySettings.showPrice ? `<td class="text-right py-5 font-semibold text-base">$${formatCurrency(sectionTotal)}</td>` : ''}
        </tr>
      `;
    }

    html += '</tbody></table>';
    return html;
  };

  // Generate the complete HTML content
  let itemsHtml = '';
  if (displaySettings.separateGroups ?? false) {
    if (groupedItems.length > 0) {
      itemsHtml += generateItemsSection(groupedItems);
    }
    if (nonGroupedItems.length > 0) {
      itemsHtml += generateItemsSection(nonGroupedItems);
    }
  } else {
    itemsHtml += generateItemsSection([...groupedItems, ...nonGroupedItems]);
  }

  // Add grand total section - conditionally show total based on showTotalCost setting
  if (displaySettings.showTotalCost !== false) {
    itemsHtml += `
      <div class="mt-8 border-t-2 border-gray-300 pt-6">
        <div class="flex justify-between items-center">
          <span class="text-xl font-bold">Total:</span>
          <span class="text-xl font-bold">$${formatCurrency(totalClientPrice)}</span>
        </div>
      </div>
    `;
  }

  // Get client email and determine if it's multiple emails
  const clientEmail = lead.client?.email || lead.clientEmail || lead.leadContact?.email || '';
  const hasMultipleEmails = clientEmail.includes(',');
  const emailLabel = hasMultipleEmails ? 'Emails' : 'Email';

  // Resolve client phone from various possible sources
  const clientPhone = lead.client?.cellPhone ||
    lead.client?.altPhone ||
    lead.siteContact?.cellPhone ||
    lead.siteContact?.altPhone ||
    lead.leadContact?.phone ||
    lead.clientPhone ||
    '';

  // Resolve rep initials: saved value on the proposal, else derived from the
  // createdBy user (or lead's assigned rep as a fallback).
  const repInitials = resolveRepInitials(
    proposal.topsailRepInitials,
    proposal.createdBy || lead.assignedTo || lead.createdBy
  );

  // Contractor block (editable in edit preview) + customer initials (captured at signing).
  const contractorSignatureImg = proposal.contractorSignature
    ? `<img src="${proposal.contractorSignature}" alt="Signature" class="h-10" style="max-height:40px; vertical-align:middle;" />`
    : '';
  const contractorName = proposal.contractorName || '';
  const contractorDate = proposal.signature?.date
    ? new Date(proposal.signature.date).toLocaleDateString()
    : (proposal.contractorSignature ? new Date().toLocaleDateString() : '');
  const customerInitials = proposal.signature?.initials || '';

  // Create template data with proper fallbacks
  const templateData = {
    'lead.clientName': clientName,
    'lead.clientEmail': clientEmail,
    'lead.clientEmailLabel': emailLabel,
    'lead.clientPhone': clientPhone,
    'lead.jobNumber': lead.jobNumber || '',
    'lead.streetAddress': jobAddress,
    'lead.city': '',
    'lead.state': '',
    'lead.zipCode': '',
    'proposal.title': proposal.title || `Proposal for ${clientName}`,
    'today': new Date().toLocaleDateString(),
    'itemsHtml': itemsHtml,
    'totalClientPrice.toFixed(2)': formatCurrency(totalClientPrice),
    'topsailRepInitials': repInitials,
    'customerInitials': customerInitials,
    'contractorName': contractorName,
    'contractorSignatureImg': contractorSignatureImg,
    'contractorDate': contractorDate,
    'signatureSection': '' // Remove the signature section - we'll use JavaScript to inject it directly
  };

  // Replace template variables with proper escaping
  let html = DEFAULT_PROPOSAL_TEMPLATE;
  Object.entries(templateData).forEach(([key, value]) => {
    const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`\\$\\{${escapedKey}\\}`, 'g');
    html = html.replace(regex, value || '');
  });

  // If a signature exists, inject the customer signature/date/name into EVERY slot
  // marked with data-sig-role (Acceptance page + Notice of Right to Cancel pages).
  if (proposal.signature && typeof document !== 'undefined') {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const setSlot = (slot, buildHtml) => {
      const inner = slot.querySelector('div');
      const target = inner || slot;
      target.innerHTML = buildHtml;
    };

    if (proposal.signature.signatureImage) {
      const imgHtml = `<img src="${proposal.signature.signatureImage}" alt="Signature" class="h-16" style="max-height:40px; vertical-align:middle;" />`;
      tempDiv.querySelectorAll('[data-sig-role="signature"]').forEach(slot => setSlot(slot, imgHtml));
    }
    if (proposal.signature.date) {
      const dateStr = new Date(proposal.signature.date).toLocaleDateString();
      tempDiv.querySelectorAll('[data-sig-role="date"]').forEach(slot => setSlot(slot, dateStr));
    }
    if (proposal.signature.name) {
      tempDiv.querySelectorAll('[data-sig-role="name"]').forEach(slot => setSlot(slot, proposal.signature.name));
    }

    html = tempDiv.innerHTML;
  }

  // Update where we use currency values - ensure all currency displays have commas.
  // NOTE: We only touch values that ALREADY look like whole-dollar or 2-decimal amounts;
  // never rewrite them into `undefined`.
  html = html.replace(/\$(\d+(?:,\d{3})*(?:\.\d{2})?)/g, (match, amount) => {
    const cleanAmount = parseFloat(amount.replace(/,/g, ''));
    if (isNaN(cleanAmount)) {
      return match;
    }
    // Preserve original formatting: if there was no decimal, keep whole-dollar form;
    // otherwise emit locale-formatted with 2 decimals.
    const hasDecimal = amount.includes('.');
    return `$${cleanAmount.toLocaleString('en-US', {
      minimumFractionDigits: hasDecimal ? 2 : 0,
      maximumFractionDigits: hasDecimal ? 2 : 0
    })}`;
  });

  // Only match `/logo/…` as a distinct path segment so uploaded photos
  // whose filenames merely contain the substring "logo" (e.g.
  // `hfslogo72_….png`) are NOT swapped for the Topsail wordmark.
  html = html.replace(
    /https:\/\/(pub-[a-f0-9]+\.r2\.dev(?:\/[^\s"'>]*)?\/logo\/[^\s"'>]+|pub-d6f1bdf0d5ad470d9760c07d060062bf\.r2\.dev\/[^\s"'>]+)/gi,
    'https://pub-7850b80959a348cea25cdb70de6e8ddc.r2.dev/logo/Topsail-logo_RGB-03.png'
  );

  return html;
};

// Create proposal
export const createProposal = createAsyncThunk(
  'proposals/createProposal',
  async ({ leadId, proposalData }, { rejectWithValue }) => {
    try {
      // Ensure all required fields are present
      if (!proposalData.title) {
        return rejectWithValue('Title is required');
      }
      if (!proposalData.emailAddress) {
        return rejectWithValue('Email address is required');
      }
      if (!proposalData.category) {
        return rejectWithValue('Category is required');
      }
      if (!leadId) {
        return rejectWithValue('Lead ID is required');
      }

      // Process data to ensure all numeric values are properly converted
      const processedData = {
        ...proposalData,
        clientPrice: Number(proposalData.clientPrice) || 0,
        builderCost: Number(proposalData.builderCost) || 0,
        estimatedProfit: Number(proposalData.estimatedProfit) || 0,
        scopeOfWork: proposalData.scopeOfWork || [],
        costCodes: proposalData.costCodes || [],
        costGroups: proposalData.costGroups || [],
        status: proposalData.status || 'draft',
        displaySettings: {
          showItem: true,
          showQuantity: false,
          showUnit: false,
          showPrice: false,
          showCostCodes: false,
          showScopeDescription: true,
          showCostCodeDescription: false,
          expandGroups: false,
          showGroupTotals: true,
          separateGroups: false,
          showSeparateTotals: true,
          showTotalCost: true,
          ...proposalData.displaySettings
        }
      };

      const response = await fetch(`${config.apiUrl}/api/proposals/leads/${leadId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(processedData)
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to create proposal');
      }

      return data;
    } catch (error) {
      console.error('Create proposal error:', error);
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Fetch proposals for a lead
export const fetchProposals = createAsyncThunk(
  'proposals/fetchProposals',
  async (leadId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/proposals/leads/${leadId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch proposals');
      }

      // Ensure signature data is included in the response
      return {
        ...data,
        proposals: data.proposals.map(proposal => ({
          ...proposal,
          signature: proposal.signature || null
        }))
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Update proposal
export const updateProposal = createAsyncThunk(
  'proposals/updateProposal',
  async ({ id, updates }, { rejectWithValue, getState }) => {
    try {
      // Get the current proposal from state
      const currentProposal = getState().proposals.currentProposal;
      
      // Ensure all numbers are properly converted
      const processedUpdates = JSON.parse(JSON.stringify(updates)); // Deep clone
      
      // Convert top-level numerical values
      if (processedUpdates.clientPrice !== undefined) {
        processedUpdates.clientPrice = Number(processedUpdates.clientPrice) || 0;
      }
      if (processedUpdates.builderCost !== undefined) {
        processedUpdates.builderCost = Number(processedUpdates.builderCost) || 0;
      }
      if (processedUpdates.estimatedProfit !== undefined) {
        processedUpdates.estimatedProfit = Number(processedUpdates.estimatedProfit) || 0;
      }
      
      // Process scope of work items
      if (processedUpdates.scopeOfWork) {
        processedUpdates.scopeOfWork = processedUpdates.scopeOfWork.map(item => {
          // Deep clone to avoid reference issues
          const processedItem = {...item};
          
          // Process the cost group if it's an object
          if (processedItem.costGroup && typeof processedItem.costGroup === 'object') {
            // Ensure all cost group numerical values are converted
            ['builderCost', 'markup', 'clientPrice', 'margin'].forEach(field => {
              if (processedItem.costGroup[field] !== undefined) {
                processedItem.costGroup[field] = Number(processedItem.costGroup[field]) || 0;
              }
            });
          }
          
          // Process the item's own numerical fields
          ['builderCost', 'markup', 'clientPrice', 'margin'].forEach(field => {
            if (processedItem[field] !== undefined) {
              processedItem[field] = Number(processedItem[field]) || 0;
            }
          });
          
          return processedItem;
        });
      }
      
      // Process cost codes items - ensure we're sending all the necessary data
      if (processedUpdates.costCodes) {
        processedUpdates.costCodes = processedUpdates.costCodes.map(item => {
          // Deep clone to avoid reference issues
          const processedItem = {...item};
          
          // Process the cost item if it's an object
          if (processedItem.costItem && typeof processedItem.costItem === 'object') {
            // Ensure all cost item numerical values are converted - handle 0 values properly
            const fields = ['quantity', 'unitCost', 'builderCost', 'markup', 'clientPrice', 'margin'];
            fields.forEach(field => {
              if (processedItem.costItem[field] !== undefined) {
                // Special handling for unitCost and builderCost to preserve 0 values
                if (field === 'unitCost' || field === 'builderCost') {
                  processedItem.costItem[field] = Number(processedItem.costItem[field]);
                  if (isNaN(processedItem.costItem[field])) {
                    processedItem.costItem[field] = 0;
                  }
                } else {
                  processedItem.costItem[field] = Number(processedItem.costItem[field]) || 0;
                }
              }
            });
            
            // Make sure we have the unit field set
            if (!processedItem.costItem.unit) {
              processedItem.costItem.unit = 'EA';
            }
            
            // Ensure we have these values as they're required
            if (processedItem.costItem.quantity === undefined) processedItem.costItem.quantity = 1;
            if (processedItem.costItem.markup === undefined) processedItem.costItem.markup = 49.25;
            if (processedItem.costItem.margin === undefined) processedItem.costItem.margin = 33;
          }
          
          // Process the item's own numerical fields
          ['quantity', 'unitCost', 'builderCost', 'markup', 'clientPrice', 'margin'].forEach(field => {
            if (processedItem[field] !== undefined) {
              // Special handling for unitCost to preserve 0 values
              if (field === 'unitCost' || field === 'builderCost') {
                processedItem[field] = Number(processedItem[field]);
                if (isNaN(processedItem[field])) {
                  processedItem[field] = 0;
                }
              } else {
                processedItem[field] = Number(processedItem[field]) || 0;
              }
            }
          });
          
          return processedItem;
        });
      }
      
      // Get the current email address from the proposal if not provided
      if (!processedUpdates.emailAddress && currentProposal) {
        processedUpdates.emailAddress = currentProposal.emailAddress;
      }

      const response = await fetch(`${config.apiUrl}/api/proposals/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(processedUpdates)
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to update proposal');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Update proposal title only
export const updateProposalTitle = createAsyncThunk(
  'proposals/updateProposalTitle',
  async ({ id, title }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/proposals/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ title })
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to update proposal title');
      }

      return { id, title, proposal: data.proposal };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Update proposal status
export const updateProposalStatus = createAsyncThunk(
  'proposals/updateProposalStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/proposals/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to update proposal status');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add these new thunks
export const fetchCostGroups = createAsyncThunk(
  'proposals/fetchCostGroups',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/proposals/cost-groups`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch cost groups');
      }

      return data.costGroups;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const fetchCostItems = createAsyncThunk(
  'proposals/fetchCostItems',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/proposals/cost-items`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch cost items');
      }

      return data.costItems;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const createCostItem = createAsyncThunk(
  'proposals/createCostItem',
  async (costItemData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/proposals/cost-items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(costItemData)
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to create cost item');
      }

      // Log the response to see what we're getting back
      console.log('Create cost item response:', data);

      return data;
    } catch (error) {
      console.error('Create cost item error:', error);
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const createCostGroup = createAsyncThunk(
  'proposals/createCostGroup',
  async (costGroupData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/proposals/cost-groups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(costGroupData)
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to create scope of work');
      }

      return data.costGroup;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const fetchCostGroupsByProposal = createAsyncThunk(
  'proposals/fetchCostGroupsByProposal',
  async (proposalId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/proposals/${proposalId}/cost-groups`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch cost groups');
      }

      return data.costGroups;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const fetchCostItemsByProposal = createAsyncThunk(
  'proposals/fetchCostItemsByProposal',
  async (proposalId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/proposals/${proposalId}/cost-items`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch cost items');
      }

      return data.costItems;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add this new thunk after other thunks
export const deleteProposal = createAsyncThunk(
  'proposals/deleteProposal',
  async (proposalId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/proposals/${proposalId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to delete proposal');
      }

      return { proposalId };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add new action to update display settings
export const updateProposalDisplaySettings = createAsyncThunk(
  'proposals/updateDisplaySettings',
  async ({ id, displaySettings }, { rejectWithValue }) => {
    try {
          // Ensure all display settings have proper boolean values
    const normalizedDisplaySettings = {
      showItem: Boolean(displaySettings.showItem !== false),
      showQuantity: displaySettings.showQuantity !== undefined ? Boolean(displaySettings.showQuantity) : false,
      showUnit: displaySettings.showUnit !== undefined ? Boolean(displaySettings.showUnit) : false,
      showPrice: displaySettings.showPrice !== undefined ? Boolean(displaySettings.showPrice) : false,
      showCostCodes: displaySettings.showCostCodes !== undefined ? Boolean(displaySettings.showCostCodes) : false,
      showScopeDescription: displaySettings.showScopeDescription !== undefined ? Boolean(displaySettings.showScopeDescription) : true,
      showCostCodeDescription: displaySettings.showCostCodeDescription !== undefined ? Boolean(displaySettings.showCostCodeDescription) : false,
      expandGroups: displaySettings.expandGroups !== undefined ? Boolean(displaySettings.expandGroups) : false,
      separateGroups: displaySettings.separateGroups !== undefined ? Boolean(displaySettings.separateGroups) : false,
      showGroupTotals: Boolean(displaySettings.showGroupTotals !== false),
      showSeparateTotals: Boolean(displaySettings.showSeparateTotals !== false),
      showTotalCost: Boolean(displaySettings.showTotalCost !== false)
    };

      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/proposals/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ displaySettings: normalizedDisplaySettings })
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to update display settings');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add this new thunk
export const releaseProposal = createAsyncThunk(
  'proposals/releaseProposal',
  async (proposalId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/proposals/${proposalId}/release`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to release proposal');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add new thunk for approving proposal by owner
export const approveProposalOwner = createAsyncThunk(
  'proposals/approveProposalOwner',
  async (proposalId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/proposals/${proposalId}/approve-owner`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to approve proposal');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add new thunk for deleting items
export const deleteProposalItem = createAsyncThunk(
  'proposals/deleteProposalItem',
  async ({ proposalId, itemId, itemType, deleteAssociatedItems = false, costGroupId = null }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/proposals/${proposalId}/items`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          itemId, 
          itemType,
          deleteAssociatedItems,
          costGroupId
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to delete item');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add this new thunk
export const fetchCostGroupsData = createAsyncThunk(
  'proposals/fetchCostGroupsData',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/proposals/cost-groups-data`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch cost groups data');
      }

      return data.costGroupsData;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add this new thunk after other thunks
export const fetchCostCodesData = createAsyncThunk(
  'proposals/fetchCostCodesData',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/proposals/cost-codes-data`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      console.log('Cost codes data response:', data);

      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch cost codes data');
      }

      return data.costCodesData;
    } catch (error) {
      console.error('Error fetching cost codes data:', error);
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add these new thunks after other thunks
export const deleteCostItem = createAsyncThunk(
  'proposals/deleteCostItem',
  async (itemId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/proposals/cost-items/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to delete cost item');
      }

      return { itemId };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const deleteCostGroup = createAsyncThunk(
  'proposals/deleteCostGroup',
  async (groupId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/proposals/cost-groups/${groupId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to delete cost group');
      }

      return { groupId };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add this new thunk after other thunks
export const deleteCostCodeItem = createAsyncThunk(
  'proposals/deleteCostCodeItem',
  async (costCodeId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/proposals/cost-items/${costCodeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to delete cost code item');
      }

      return { costCodeId };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add new thunk for manually approving proposal
export const manualApproveProposal = createAsyncThunk(
  'proposals/manualApproveProposal',
  async (proposalId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/proposals/${proposalId}/manual-approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to manually approve proposal');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add new thunk for updating released proposal
export const updateReleasedProposal = createAsyncThunk(
  'proposals/updateReleasedProposal',
  async ({ id, updates }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/proposals/${id}/update-released`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to update released proposal');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add new thunk for duplicating a proposal
export const duplicateProposal = createAsyncThunk(
  'proposals/duplicateProposal',
  async (proposalId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/proposals/${proposalId}/duplicate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to duplicate proposal');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Fetch all proposals for dashboard
export const fetchAllProposals = createAsyncThunk(
  'proposals/fetchAllProposals',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { projectManagerId, startDate, endDate } = params;
      const token = localStorage.getItem('token');
      
      const queryParams = new URLSearchParams();
      if (projectManagerId) {
        queryParams.append('projectManagerId', projectManagerId);
      }
      if (startDate) {
        queryParams.append('startDate', startDate);
      }
      if (endDate) {
        queryParams.append('endDate', endDate);
      }
      
      const url = `${config.apiUrl}/api/proposals/all${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch all proposals');
      }

      return {
        ...data,
        proposals: data.proposals.map(proposal => ({
          ...proposal,
          signature: proposal.signature || null
        }))
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Upload photos to a cost group
export const uploadCostGroupPhotos = createAsyncThunk(
  'proposals/uploadCostGroupPhotos',
  async ({ proposalId, costGroupId, files }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      // Append all files to the form data
      files.forEach(file => {
        formData.append('photos', file);
      });

      const response = await fetch(
        `${config.apiUrl}/api/proposals/${proposalId}/cost-groups/${costGroupId}/photos`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to upload photos');
      }

      return { costGroupId, photos: data.photos };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Fetch photos for a cost group
export const fetchCostGroupPhotos = createAsyncThunk(
  'proposals/fetchCostGroupPhotos',
  async ({ proposalId, costGroupId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(
        `${config.apiUrl}/api/proposals/${proposalId}/cost-groups/${costGroupId}/photos`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch photos');
      }

      return { costGroupId, photos: data.photos };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Delete a photo from a cost group
export const deleteCostGroupPhoto = createAsyncThunk(
  'proposals/deleteCostGroupPhoto',
  async ({ proposalId, costGroupId, photoId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(
        `${config.apiUrl}/api/proposals/${proposalId}/cost-groups/${costGroupId}/photos/${photoId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to delete photo');
      }

      return { costGroupId, photoId };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

const proposalsSlice = createSlice({
  name: 'proposals',
  initialState: {
    proposals: [],
    currentProposal: null,
    costGroups: [],
    costItems: [],
    loading: false,
    error: null,
    costGroupsData: [],
    costCodesData: []
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentProposal: (state, action) => {
      state.currentProposal = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createProposal.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProposal.fulfilled, (state, action) => {
        state.loading = false;
        state.proposals.push(action.payload.proposal);
        state.currentProposal = action.payload.proposal;
        showToast('Proposal created successfully', 'success');
      })
      .addCase(createProposal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(fetchProposals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProposals.fulfilled, (state, action) => {
        state.loading = false;
        state.proposals = action.payload.proposals;
      })
      .addCase(fetchProposals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(updateProposal.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProposal.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.proposals.findIndex(p => p._id === action.payload.proposal._id);
        if (index !== -1) {
          state.proposals[index] = action.payload.proposal;
        }
        state.currentProposal = action.payload.proposal;
        showToast('Proposal updated successfully', 'success');
      })
      .addCase(updateProposal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(updateProposalTitle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProposalTitle.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.proposals.findIndex(p => p._id === action.payload.id);
        if (index !== -1) {
          state.proposals[index].title = action.payload.title;
        }
        if (state.currentProposal?._id === action.payload.id) {
          state.currentProposal.title = action.payload.title;
        }
        showToast('Proposal title updated successfully', 'success');
      })
      .addCase(updateProposalTitle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(updateProposalStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProposalStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.proposals.findIndex(p => p._id === action.payload.proposal._id);
        if (index !== -1) {
          state.proposals[index] = action.payload.proposal;
        }
        state.currentProposal = action.payload.proposal;
        showToast('Proposal status updated successfully', 'success');
      })
      .addCase(updateProposalStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(fetchCostGroups.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCostGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.costGroups = action.payload || [];
      })
      .addCase(fetchCostGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCostItems.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCostItems.fulfilled, (state, action) => {
        state.loading = false;
        state.costItems = action.payload || [];
      })
      .addCase(fetchCostItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createCostItem.pending, (state) => {
        state.loading = true;
      })
      .addCase(createCostItem.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.proposal) {
          if (state.currentProposal?._id === action.payload.proposal._id) {
            state.currentProposal = action.payload.proposal;
          }
          const index = state.proposals.findIndex(p => p._id === action.payload.proposal._id);
          if (index !== -1) {
            state.proposals[index] = action.payload.proposal;
          }
        }
        showToast('Cost code created successfully', 'success');
      })
      .addCase(createCostItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(createCostGroup.pending, (state) => {
        state.loading = true;
      })
      .addCase(createCostGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.costGroups.push(action.payload);
        showToast('Scope of work created successfully', 'success');
      })
      .addCase(createCostGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(fetchCostGroupsByProposal.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCostGroupsByProposal.fulfilled, (state, action) => {
        state.loading = false;
        state.costGroups = action.payload || [];
      })
      .addCase(fetchCostGroupsByProposal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCostItemsByProposal.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCostItemsByProposal.fulfilled, (state, action) => {
        state.loading = false;
        state.costItems = action.payload || [];
      })
      .addCase(fetchCostItemsByProposal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteProposal.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProposal.fulfilled, (state, action) => {
        state.loading = false;
        state.proposals = state.proposals.filter(
          proposal => proposal._id !== action.payload.proposalId
        );
        showToast('Proposal deleted successfully', 'success');
      })
      .addCase(deleteProposal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(updateProposalDisplaySettings.fulfilled, (state, action) => {
        const index = state.proposals.findIndex(p => p._id === action.payload.proposal._id);
        if (index !== -1) {
          state.proposals[index] = action.payload.proposal;
        }
        state.currentProposal = action.payload.proposal;
        showToast('Display settings updated successfully', 'success');
      })
      .addCase(releaseProposal.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(releaseProposal.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.proposals.findIndex(p => p._id === action.payload.proposal._id);
        if (index !== -1) {
          state.proposals[index] = action.payload.proposal;
        }
        showToast('Proposal released successfully', 'success');
      })
      .addCase(releaseProposal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(approveProposalOwner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveProposalOwner.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.proposals.findIndex(p => p._id === action.payload.proposal._id);
        if (index !== -1) {
          state.proposals[index] = action.payload.proposal;
        }
        showToast('Proposal approved successfully', 'success');
      })
      .addCase(approveProposalOwner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(deleteProposalItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProposalItem.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.proposals.findIndex(p => p._id === action.payload.proposal._id);
        if (index !== -1) {
          state.proposals[index] = action.payload.proposal;
        }
        state.currentProposal = action.payload.proposal;
        showToast('Item deleted successfully', 'success');
      })
      .addCase(deleteProposalItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(fetchCostGroupsData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCostGroupsData.fulfilled, (state, action) => {
        state.loading = false;
        state.costGroupsData = action.payload;
      })
      .addCase(fetchCostGroupsData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCostCodesData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCostCodesData.fulfilled, (state, action) => {
        state.loading = false;
        state.costCodesData = action.payload;
      })
      .addCase(fetchCostCodesData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteCostItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCostItem.fulfilled, (state, action) => {
        state.loading = false;
        state.costItems = state.costItems.filter(item => item._id !== action.payload.itemId);
        showToast('Cost item deleted successfully', 'success');
      })
      .addCase(deleteCostItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(deleteCostGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCostGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.costGroups = state.costGroups.filter(group => group._id !== action.payload.groupId);
        showToast('Cost group deleted successfully', 'success');
      })
      .addCase(deleteCostGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(deleteCostCodeItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCostCodeItem.fulfilled, (state, action) => {
        state.loading = false;
        showToast('Cost code item deleted successfully', 'success');
      })
      .addCase(deleteCostCodeItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(manualApproveProposal.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(manualApproveProposal.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.proposals.findIndex(p => p._id === action.payload.proposal._id);
        if (index !== -1) {
          state.proposals[index] = action.payload.proposal;
        }
        showToast('Proposal manually approved successfully', 'success');
      })
      .addCase(manualApproveProposal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(updateReleasedProposal.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateReleasedProposal.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.proposals.findIndex(p => p._id === action.payload.proposal._id);
        if (index !== -1) {
          state.proposals[index] = action.payload.proposal;
        }
        state.currentProposal = action.payload.proposal;
        showToast('Released proposal updated successfully', 'success');
      })
      .addCase(updateReleasedProposal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(fetchAllProposals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllProposals.fulfilled, (state, action) => {
        state.loading = false;
        state.proposals = action.payload.proposals;
      })
      .addCase(fetchAllProposals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(duplicateProposal.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(duplicateProposal.fulfilled, (state, action) => {
        state.loading = false;
        state.proposals.push(action.payload.proposal);
        showToast('Proposal duplicated successfully', 'success');
      })
      .addCase(duplicateProposal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(uploadCostGroupPhotos.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadCostGroupPhotos.fulfilled, (state, action) => {
        state.loading = false;
        // Photos are handled in the component state
      })
      .addCase(uploadCostGroupPhotos.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(fetchCostGroupPhotos.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCostGroupPhotos.fulfilled, (state, action) => {
        state.loading = false;
        // Photos are handled in the component state
      })
      .addCase(fetchCostGroupPhotos.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(deleteCostGroupPhoto.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCostGroupPhoto.fulfilled, (state, action) => {
        state.loading = false;
        // Photos are handled in the component state
      })
      .addCase(deleteCostGroupPhoto.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      });
  }
});

export const { clearError, setCurrentProposal } = proposalsSlice.actions;
export default proposalsSlice.reducer; 